import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  InternalPage,
  Section,
  Form,
  ResetFormHandler,
} from 'theSource/components';
import {
  getFormField,
  getInitialValues,
  getValidationSchema,
  useApiMutation,
} from 'theSource/utils';
import {
  ErrorResponseDto,
  FormFieldsCategory,
  ResponseDto,
  FormValues,
} from 'theSource/types';
import {
  COMMUNICATION_MODE_FIELD_NAME,
  mapSubscriberEditDto,
  UPDATE_SUBSCRIBER_QUERY_KEY,
} from './utils';
import { adjustCommunicationModeValidation } from 'theSource/utils/fieldUtils';
import { SubscriberEditRequestDto } from './types';
import { ROUTES } from 'theSource/app/routes';
import { GET_SUBSCRIBER_QUERY_KEY } from 'theSource/queries';
import { BackLink } from 'enrollment/components';
import { RequiredLegend } from 'enrollment/features/Shared/RequiredLegend/RequiredLegend';
import { ToastDialogProvider } from 'enrollment/components/Shared/ToastDialogContext';

export interface MyProfileDetailsEditPageProps {
  categories: FormFieldsCategory[];
}

export const MyProfileDetailsEditPage = ({
  categories,
}: MyProfileDetailsEditPageProps) => {
  const navigate = useNavigate();
  const navigateBack = () => navigate(ROUTES.additional.profile.myProfile.path);
  const { t } = useTranslation();
  const initialCommunicationMode = categories
    .flatMap((category) => category.fields)
    .find((field) => field.fieldName === COMMUNICATION_MODE_FIELD_NAME)
    ?.value as string;

  const [preferredCommunicationMode, setPreferredCommunicationMode] = useState(
    initialCommunicationMode
  );

  const { isLoading: isEditing, mutate: updateSubscriber } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    SubscriberEditRequestDto
  >({
    queryKey: UPDATE_SUBSCRIBER_QUERY_KEY,
    queryKeysToInvalidate: GET_SUBSCRIBER_QUERY_KEY,
    invalidateQueriesCallback: () => navigateBack(),
    method: 'put',
    endpoint: '/subscriber',
    getErrorMessage: (error) => error.response?.data?.details,
  });

  const handleSubmit = (values: FormValues, resetForm: ResetFormHandler) =>
    updateSubscriber(mapSubscriberEditDto(categories ?? [], values), {
      onSuccess: () => resetForm(),
    });

  const validationSchema = useMemo(
    () =>
      getValidationSchema(
        adjustCommunicationModeValidation(
          preferredCommunicationMode,
          categories
        ),
        true
      ),
    [categories, preferredCommunicationMode]
  );

  const initialValues = useMemo(
    () => getInitialValues(categories),
    [categories]
  );

  const handleChange = (values: FormValues) => {
    const communicationModeNextValue = values.communication_mode as string;
    setPreferredCommunicationMode(communicationModeNextValue);
  };

  return (
    <ToastDialogProvider>
      <InternalPage
        testId="profile-details-edit-page"
        lookupCode="enroll_personal"
        CustomActionComponent={<BackLink />}
        customActionAlignment="left"
      >
        <div className="mt-3">
          <RequiredLegend />
        </div>

        <Form<FormValues>
          isLoading={isEditing}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onChange={handleChange}
          onSubmitClick={handleSubmit}
          onCancelClick={navigateBack}
        >
          {categories.map((category) => (
            <Section key={category.name} name={category.name}>
              {category.fields.map(getFormField)}
            </Section>
          ))}
        </Form>
      </InternalPage>
    </ToastDialogProvider>
  );
};
